import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, CardHeader, Input, Modal, ModalFooter, Pagination } from '@/components/common';
import {
  BatchPreview,
  BatchStatusBadge,
  HospitalListUpload,
} from '@/components/directory/HospitalListUpload';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { getErrorMessage } from '@/services';
import { hospitalNetworkApi, type ImportBatchDetail, type InsurerHospital } from '@/services/hospitalNetwork';
import { useAuthStore } from '@/store/auth';
import { formatDateTime } from '@/utils';

/**
 * DQ-346: an insurer's own cashless hospital network.
 *
 * "My hospitals" is this insurer's list (toggle cashless, switch a hospital
 * off); "Upload list" takes a CSV/XLSX through a dry-run preview. The server
 * scopes every call to the login's own insurer; insurer employees read only.
 */

type Tab = 'hospitals' | 'upload';
const PAGE_SIZE = 20;

const SCOPE_LABEL: Record<string, string> = {
  all: 'All insurers',
  unique: 'Only you',
  partial: 'Some insurers',
  none: '—',
};

function MyHospitals({ canWrite }: { canWrite: boolean }) {
  const search = useDebouncedSearch('', 300);
  const [status, setStatus] = useState('active');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['insurer-hospitals', search.debouncedTerm, status, page],
    queryFn: () => hospitalNetworkApi.listMappings({ q: search.debouncedTerm, status, page }),
  });
  const update = useMutation({
    mutationFn: ({ row, patch }: { row: InsurerHospital; patch: Partial<InsurerHospital> }) =>
      hospitalNetworkApi.updateMapping(row.id, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insurer-hospitals'] }),
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const deactivate = useMutation({
    mutationFn: (row: InsurerHospital) => hospitalNetworkApi.deactivateMapping(row.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insurer-hospitals'] });
      toast.success('Hospital removed from your network. The hospital stays in the directory.');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
  const [removing, setRemoving] = useState<InsurerHospital | null>(null);
  const rows = data?.results ?? [];

  return (
    <Card>
      <CardHeader title="My hospitals" subtitle={`${data?.count ?? 0} on your network list`} />
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Input
          name="mapping_search"
          placeholder="Search by name, city, pincode or your code"
          value={search.term}
          onChange={(e) => {
            search.setTerm(e.target.value);
            setPage(1);
          }}
        />
        <select
          aria-label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="">All</option>
        </select>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-red-600">Your hospitals could not be loaded: {getErrorMessage(error)}</p>
      ) : isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No hospitals yet. Upload your list to start.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-3 font-medium">Hospital</th>
                <th className="py-2 pr-3 font-medium">City / Pincode</th>
                <th className="py-2 pr-3 font-medium">Your code</th>
                <th className="py-2 pr-3 font-medium">Network</th>
                <th className="py-2 pr-3 font-medium">Cashless</th>
                <th className="py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="py-2 pr-3 text-gray-900 dark:text-white">{row.hospital_name}</td>
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                    {row.city_name} {row.pincode}
                  </td>
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">{row.insurer_hospital_code || '—'}</td>
                  <td className="py-2 pr-3">
                    <Badge size="sm">{SCOPE_LABEL[row.network_scope] ?? row.network_scope}</Badge>
                  </td>
                  <td className="py-2 pr-3">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        aria-label={`Cashless at ${row.hospital_name}`}
                        checked={row.cashless_enabled}
                        disabled={!canWrite || update.isPending}
                        onChange={(e) => update.mutate({ row, patch: { cashless_enabled: e.target.checked } })}
                      />
                      {row.cashless_enabled ? 'Yes' : 'No'}
                    </label>
                  </td>
                  <td className="py-2 text-right">
                    {canWrite && row.status === 'active' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRemoving(row)}
                      >
                        Remove
                      </Button>
                    )}
                    {canWrite && row.status === 'inactive' && (
                      <Button size="sm" variant="ghost" onClick={() => update.mutate({ row, patch: { status: 'active' } })}>
                        Restore
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(data?.count ?? 0) > PAGE_SIZE && (
        <Pagination
          currentPage={page}
          totalPages={Math.ceil((data?.count ?? 0) / PAGE_SIZE)}
          totalItems={data?.count ?? 0}
          itemsPerPage={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
      <Modal isOpen={removing !== null} onClose={() => setRemoving(null)} title="Remove hospital" size="sm">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Remove <span className="font-medium">{removing?.hospital_name}</span> from your network? The hospital stays in
          the directory and you can restore it later.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setRemoving(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            isLoading={deactivate.isPending}
            onClick={() => {
              if (removing) deactivate.mutate(removing, { onSettled: () => setRemoving(null) });
            }}
          >
            Remove
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}

export function BatchHistory({ onOpen, insurer }: { onOpen: (id: string) => void; insurer?: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['hospital-import-batches', insurer ?? ''],
    queryFn: () => hospitalNetworkApi.listBatches({ insurer }),
  });
  const rows = data?.results ?? [];
  return (
    <Card>
      <CardHeader title="Uploaded lists" />
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No lists uploaded yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((batch) => (
            <li key={batch.id} className="py-2 flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-white break-all">{batch.original_name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {batch.insurer_name} · {formatDateTime(batch.created_at)} · {batch.row_count} rows
                  {batch.pending_review_count > 0 ? ` · ${batch.pending_review_count} awaiting review` : ''}
                </p>
              </div>
              <BatchStatusBadge status={batch.status} label={batch.status_display} />
              <Button size="sm" variant="outline" onClick={() => onOpen(batch.id)}>
                Open
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function HospitalNetworkPage() {
  const { user } = useAuthStore();
  const canWrite = user?.role === 'insurer_admin';
  const [tab, setTab] = useState<Tab>('hospitals');
  const [batch, setBatch] = useState<ImportBatchDetail | null>(null);

  const open = async (id: string) => {
    try {
      setBatch(await hospitalNetworkApi.getBatch(id));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader
        title="Hospital Network"
        subtitle="Your cashless hospital list, as customers see it in the hospital locator"
      />
      <div className="mb-4 flex gap-2" role="tablist">
        {(
          [
            ['hospitals', 'My hospitals'],
            ['upload', 'Upload list'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              tab === key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'hospitals' ? (
        <MyHospitals canWrite={canWrite} />
      ) : (
        <div className="space-y-6">
          {canWrite && <HospitalListUpload onPreview={setBatch} />}
          {batch && <BatchPreview batch={batch} canWrite={canWrite} onChanged={setBatch} />}
          <BatchHistory onOpen={open} />
        </div>
      )}
    </div>
  );
}
